import { campaignInlineStyle, campaignBaseColors } from './campaign-style.js';

const decode = (value) =>
  String(value).replace(
    /&(amp|quot|apos|lt|gt|nbsp|#x[0-9a-f]+|#[0-9]+);/gi,
    (raw, key) => {
      const named = {
        amp: '&',
        quot: '"',
        apos: "'",
        lt: '<',
        gt: '>',
        nbsp: '\u00a0'
      };
      if (key[0] !== '#') return named[key.toLowerCase()] || raw;
      const n =
        key[1].toLowerCase() === 'x'
          ? parseInt(key.slice(2), 16)
          : Number(key.slice(1));
      return Number.isInteger(n) &&
        n > 0 &&
        n <= 0x10ffff &&
        !(n >= 0xd800 && n <= 0xdfff)
        ? String.fromCodePoint(n)
        : '';
    }
  );
// Restricted campaign markup only. Never interpolate CMS attributes or executable HTML.
const escape = (value) =>
  String(value).replace(
    /[&<>"']/g,
    (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[
        c
      ]
  );
export function campaignMarkup(value) {
  const source = String(value || '')
    .slice(0, 10000)
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(
      /<(script|style|iframe|object|svg|math)\b[^>]*>[\s\S]*?<\/\1\s*>/gi,
      ''
    );
  const allowed = new Set([
    'div',
    'p',
    'strong',
    'b',
    'em',
    'i',
    'code',
    'span',
    'br',
    'a'
  ]);
  let output = '',
    cursor = 0,
    count = 0;
  const stack = [];
  const closeThrough = (index) => {
    while (stack.length > index) output += `</${stack.pop().tag}>`;
  };
  const findTag = (name) => {
    for (let i = stack.length - 1; i >= 0; i--)
      if (stack[i].tag === name) return i;
    return -1;
  };
  const tokens =
    /<\/?[a-z][a-z0-9]*(?:\s+(?:[^>"']|"[^"]*"|'[^']*')*)?\s*\/?>/gi;
  for (const match of source.matchAll(tokens)) {
    if (++count > 256 || stack.length > 16)
      return escape(decode(source.replace(/<[^>]*>/g, ' ')));
    output += escape(decode(source.slice(cursor, match.index)));
    cursor = match.index + match[0].length;
    const tag = match[0].match(/^<(\/?)([a-z0-9]+)/i);
    const name = tag[2].toLowerCase();
    if (!allowed.has(name)) continue;
    if (tag[1]) {
      const index = findTag(name);
      if (index >= 0) closeThrough(index);
      continue;
    }
    if (name === 'br') {
      output += '<br>';
      continue;
    }
    // Match browser paragraph/anchor rules before deriving inherited colors.
    if (name === 'p' || name === 'div') {
      const index = findTag('p');
      if (index >= 0) closeThrough(index);
    }
    if (name === 'a') {
      const index = findTag('a');
      if (index >= 0) closeThrough(index);
    }
    const attrs = {};
    for (const attr of match[0].matchAll(
      /\s+([a-z][a-z0-9:-]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/gi
    )) {
      const key = attr[1].toLowerCase();
      if (['style', 'href'].includes(key) && !Object.hasOwn(attrs, key))
        attrs[key] = decode(attr.slice(2).find((x) => x !== undefined) || '');
    }
    const styling = campaignInlineStyle(
      attrs.style,
      stack.at(-1)?.context || campaignBaseColors,
      name
    );
    let attributes = styling.css ? ` style="${escape(styling.css)}"` : '';
    if (name === 'a') {
      try {
        const href = attrs.href || '';
        const url = new URL(href, 'https://www.idmanify.com');
        if (
          !href.trim() ||
          !['https:', 'http:'].includes(url.protocol) ||
          url.username ||
          url.password
        )
          throw Error();
        attributes += ` href="${escape(url.href)}" rel="noopener noreferrer"`;
      } catch {
        /* Invalid links keep their text, not their destination. */
      }
    }
    output += `<${name}${attributes}>`;
    if (/\/\s*>$/.test(match[0])) output += `</${name}>`;
    else stack.push({ tag: name, context: styling.context });
  }
  output += escape(decode(source.slice(cursor)));
  closeThrough(0);
  return output;
}
export function campaignProgress(value) {
  const number = Number(value);
  return Number.isFinite(number)
    ? Math.round(Math.min(100, Math.max(0, number)))
    : 0;
}
export function campaignCountdown(deadline, now) {
  if (!Number.isFinite(deadline) || deadline <= 0) return null;
  if (now === null) return ['--', '--', '--', '--'];
  const seconds = Math.max(0, Math.ceil((deadline - now) / 1000));
  return [
    Math.floor(seconds / 86400),
    Math.floor(seconds / 3600) % 24,
    Math.floor(seconds / 60) % 60,
    seconds % 60
  ].map((x) => String(x).padStart(2, '0'));
}

export function parseCampaignDeadline(value) {
  if (
    value === null ||
    value === undefined ||
    String(value).trim() === '' ||
    String(value).trim() === '0'
  )
    return null;
  const raw = String(value).trim();
  if (/^\d{10,13}$/.test(raw)) {
    const number = Number(raw);
    return raw.length === 10 ? number * 1000 : number;
  }
  const parsed = new Date(
    raw.includes(' ') ? raw.replace(' ', 'T') : raw
  ).getTime();
  return Number.isFinite(parsed) ? parsed : null;
}

export function campaignStoreEvent(href) {
  try {
    const url = new URL(href);
    return url.protocol === 'https:' &&
      url.hostname === 'play.google.com' &&
      url.pathname === '/store/apps/details' &&
      url.searchParams.get('id') === 'com.idmanify.idmanify'
      ? 'app_google_play_click'
      : null;
  } catch {
    return null;
  }
}
